/**
 * Loading one baked world region (`data/baked/world/<REGION>.json`, served
 * at `/baked/…`) — the fetch plumbing only. Body types are declared locally
 * rather than imported from `@red-opus/baker` on purpose, mirroring
 * `three/baked-model.ts`'s `BakedModelBody`: the full `@red-opus/baker`
 * barrel pulls in Node-only encoders (`node:zlib`/`node:crypto`, see
 * docs/engineering/lessons.md's "packages/baker leaking node:zlib" entry),
 * and this shape is a stable, independent contract with `packages/baker/src/world-bake.ts`'s
 * `BakedWorldRegion` body either way — a structural duplicate here is exactly
 * the "narrow, browser-safe copy of a Node-side type" the lesson calls for,
 * not the identical-shape-across-siblings duplication a different lesson
 * warns against (this package boundary is the point, not an accident).
 */

export interface WorldPlacementBody {
  readonly objectId: number;
  readonly objectRef: string | null;
  readonly x: number;
  readonly y: number;
  readonly rotationDegrees: number;
  readonly raw: { readonly unknown4: number; readonly unknown10: number };
}

export interface WorldAerodromeBody {
  readonly name: string;
  readonly mapX: number;
  readonly mapY: number;
}

export interface WorldBoundsBody {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

/** One `.GI` footprint corner, already in metres (`packages/baker/src/world-bake.ts`'s `WorldGroundVertex`). */
export interface WorldGroundVertexBody {
  readonly xM: number;
  readonly yM: number;
  readonly edgeNormalX: number;
  readonly edgeNormalY: number;
}

/** One `.GI` facet, already in metres (`WorldGroundFacet`). */
export interface WorldGroundFacetBody {
  readonly baseHeightM: number;
  readonly gradientX: number;
  readonly gradientY: number;
  readonly refXM: number;
  readonly refYM: number;
  readonly tanScale: number;
  readonly vertices: readonly WorldGroundVertexBody[];
}

/** One placed object's ground-collision surface, in the placement's own local frame (`WorldGroundObject`). */
export interface WorldGroundObjectBody {
  readonly extentXM: number;
  readonly extentYM: number;
  readonly boundingBoxOnly: boolean;
  readonly facets: readonly WorldGroundFacetBody[];
}

export interface WorldRegionBody {
  readonly region: string;
  readonly regionPairingInferred: boolean;
  readonly source: { readonly wldPath: string; readonly tblPath: string };
  readonly bounds: WorldBoundsBody;
  readonly placements: readonly WorldPlacementBody[];
  readonly aerodromes: readonly WorldAerodromeBody[];
  /** `objectId -> ground surface` (`world-bake.ts`'s `BakedWorldRegion.groundObjects`); `{}` if the region has none. */
  readonly groundObjects: Readonly<Record<string, WorldGroundObjectBody>>;
}

/**
 * Cached by `path`: there are only six baked world regions total (five
 * campaign maps + the practice range), so this never needs an eviction
 * policy the way `three/lru-cache.ts` does for GPU-backed content — it's
 * plain JSON, and the whole corpus fits comfortably in memory at once. The
 * cache holds the *promise*, not just the resolved body, so two callers
 * racing to load the same region (e.g. `/assets`' World viewer re-selecting
 * a region it already showed this session) share one fetch instead of
 * issuing a second one before the first settles.
 *
 * Exists because `/assets`' World viewer previously refetched a region's
 * JSON from the network on every switch back to it — `createResource`
 * memoizes only *while its source key is unchanged*, not across the
 * region-A -> region-B -> region-A pattern switching categories/regions
 * produces (roadmap item 14).
 */
const regionCache = new Map<string, Promise<WorldRegionBody>>();

export async function loadWorldRegion(path: string): Promise<WorldRegionBody> {
  const cached = regionCache.get(path);
  if (cached) return cached;

  const promise = fetch(`/baked/${path}`).then(async (response) => {
    if (!response.ok) {
      throw new Error(`failed to load world region ${path}: ${response.status} ${response.statusText}`);
    }
    return (await response.json()) as WorldRegionBody;
  });
  // A failed fetch must not poison the cache — the next call should retry,
  // not replay the same rejection forever.
  promise.catch(() => regionCache.delete(path));
  regionCache.set(path, promise);
  return promise;
}
